import React, { useEffect, useState } from 'react';
import { pmAdminApi, CONTACT_ROLES } from '../../lib/pmApi';
import { Building2, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import PoSettingsForm from '../../components/pm/PoSettingsForm';

const emptyContact = { role: 'property_manager', name: '', email: '', phone: '' };
const emptyForm = {
  company_id: '', name: '', property_code: '', address: '', city: '', state: '', zip: '',
  unit_count: 0, auto_approval_limit: '', notes_internal: '', contacts: [],
  po_settings: {},
};

const PmProperties = () => {
  const [companies, setCompanies] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => Promise.all([
    pmAdminApi.companies.list(),
    pmAdminApi.properties.list(filterCompanyId || undefined),
  ]).then(([co, pr]) => { setCompanies(co); setProperties(pr); });
  useEffect(() => { load(); }, [filterCompanyId]);

  const save = async () => {
    const body = {
      ...form,
      unit_count: Number(form.unit_count) || 0,
      auto_approval_limit: form.auto_approval_limit === '' ? null : Number(form.auto_approval_limit),
    };
    if (edit) await pmAdminApi.properties.update(edit, body);
    else await pmAdminApi.properties.create(body);
    setEdit(null); setForm(emptyForm); load();
  };

  const startEdit = (p) => { setEdit(p.id); setForm({ ...emptyForm, ...p, auto_approval_limit: p.auto_approval_limit ?? '', contacts: p.contacts || [] }); };
  const addContact = () => setForm((f) => ({ ...f, contacts: [...(f.contacts || []), { ...emptyContact }] }));
  const updateContact = (i, k, v) => setForm((f) => { const arr = [...(f.contacts || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...f, contacts: arr }; });
  const removeContact = (i) => setForm((f) => ({ ...f, contacts: (f.contacts || []).filter((_, idx) => idx !== i) }));
  const archive = async (p) => { if (window.confirm(`Archive ${p.name}?`)) { await pmAdminApi.properties.archive(p.id); load(); } };

  return (
    <div data-testid="admin-pm-properties" className="p-6 max-w-7xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY MGMT</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Properties</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Each property belongs to one parent company. Property-level approval limits override company defaults.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select data-testid="pm-prop-filter" value={filterCompanyId} onChange={(e) => setFilterCompanyId(e.target.value)} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
            <option value="">All companies</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button data-testid="pm-prop-new" onClick={() => { setEdit(null); setForm(emptyForm); }} className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 rounded font-medium hover:bg-[#F3E5AB] inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Property</button>
        </div>
      </div>

      <div className="grid xl:grid-cols-5 gap-4">
        {/* List */}
        <div className="xl:col-span-3 space-y-2">
          {properties.map((p) => (
            <div key={p.id} data-testid={`pm-prop-row-${p.id}`} className={`bg-[#141414] border border-[#262626] rounded p-4 flex items-start gap-3 ${p.active === false ? 'opacity-50' : ''}`}>
              <div className="w-10 h-10 rounded bg-[#D4AF37]/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#D4AF37]" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium">{p.name} <span className="text-[#525252] text-xs font-mono ml-2">{p.property_code}</span></p>
                <p className="text-[#A3A3A3] text-xs">{p.company_name} · {p.address}, {p.city}, {p.state} {p.zip} · {p.unit_count || 0} units</p>
                {typeof p.auto_approval_limit === 'number' && <p className="text-[#D4AF37] text-xs flex items-center gap-1 mt-0.5"><ShieldCheck className="w-3 h-3" /> Property limit ${p.auto_approval_limit.toLocaleString()} (overrides company)</p>}
                {p.notes_internal && <p className="text-[#525252] text-xs mt-1 italic">📌 {p.notes_internal.slice(0, 120)}{p.notes_internal.length > 120 ? '…' : ''}</p>}
              </div>
              <button onClick={() => startEdit(p)} className="text-[#A3A3A3] hover:text-[#D4AF37] text-xs">Edit</button>
              <button onClick={() => archive(p)} className="text-[#525252] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {properties.length === 0 && <p className="text-[#525252]">No properties yet.</p>}
        </div>

        {/* Form */}
        <div className="xl:col-span-2 bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3 max-h-[80vh] overflow-y-auto">
          <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em]">{edit ? 'EDIT PROPERTY' : 'NEW PROPERTY'}</p>
          <select data-testid="pm-prop-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
            <option value="">Select parent company…</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input data-testid="pm-prop-name" placeholder="Property name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          <input data-testid="pm-prop-code" placeholder="Property code (unique within company)" value={form.property_code} onChange={(e) => setForm({ ...form, property_code: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          <input placeholder="Street address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <input placeholder="Zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label>
              <span className="text-[#A3A3A3] text-xs">Unit count</span>
              <input type="number" min="0" value={form.unit_count} onChange={(e) => setForm({ ...form, unit_count: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            </label>
            <label>
              <span className="text-[#A3A3A3] text-xs">Property approval limit (override)</span>
              <input data-testid="pm-prop-limit" type="number" min="0" placeholder="Inherit company" value={form.auto_approval_limit} onChange={(e) => setForm({ ...form, auto_approval_limit: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            </label>
          </div>

          <div>
            <p className="text-[#A3A3A3] text-xs flex items-center justify-between mb-2">
              <span>Contacts (multi-role)</span>
              <button onClick={addContact} className="text-[#D4AF37] hover:text-[#F3E5AB] text-xs">+ Add</button>
            </p>
            <div className="space-y-2">
              {(form.contacts || []).map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 items-start">
                  <select value={c.role} onChange={(e) => updateContact(i, 'role', e.target.value)} className="col-span-3 bg-[#0A0A0A] border border-[#262626] rounded h-9 px-2 text-white text-xs">
                    {CONTACT_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <input placeholder="Name" value={c.name} onChange={(e) => updateContact(i, 'name', e.target.value)} className="col-span-3 bg-[#0A0A0A] border border-[#262626] rounded h-9 px-2 text-white text-xs" />
                  <input placeholder="Email" value={c.email || ''} onChange={(e) => updateContact(i, 'email', e.target.value)} className="col-span-3 bg-[#0A0A0A] border border-[#262626] rounded h-9 px-2 text-white text-xs" />
                  <input placeholder="Phone" value={c.phone || ''} onChange={(e) => updateContact(i, 'phone', e.target.value)} className="col-span-2 bg-[#0A0A0A] border border-[#262626] rounded h-9 px-2 text-white text-xs" />
                  <button onClick={() => removeContact(i)} className="text-[#525252] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[#A3A3A3] text-xs mb-1 flex items-center gap-1">📌 Internal NexFlow Notes <span className="text-[#525252]">(never shown to PMs)</span></p>
            <textarea data-testid="pm-prop-internal-notes" rows={3} value={form.notes_internal} onChange={(e) => setForm({ ...form, notes_internal: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-2 text-white text-sm" placeholder="Gate codes, difficult tenant flags, sewer history, etc." />
          </div>

          <PoSettingsForm scope="property" value={form.po_settings || {}} onChange={(po) => setForm({ ...form, po_settings: po })} />

          <button data-testid="pm-prop-save" onClick={save} className="w-full bg-[#D4AF37] text-[#0A0A0A] h-10 rounded font-medium hover:bg-[#F3E5AB]">{edit ? 'Save changes' : 'Create property'}</button>
          {edit && <button onClick={() => { setEdit(null); setForm(emptyForm); }} className="w-full text-[#525252] text-xs">Cancel</button>}
        </div>
      </div>
    </div>
  );
};

export default PmProperties;
